import { X } from 'lucide-react'
import type { ColumnDef } from './types'
import type { FilterValue, TableState } from './state'

interface FilterChipsProps<TRow> {
  columns: ColumnDef<TRow>[]
  state: TableState
  onRemoveFilter: (key: string) => void
  onClearSearch: () => void
  onClearAll: () => void
}

export function FilterChips<TRow>({
  columns,
  state,
  onRemoveFilter,
  onClearSearch,
  onClearAll,
}: FilterChipsProps<TRow>) {
  const colByKey = new Map(columns.map((c) => [c.key, c]))
  const activeKeys = Object.keys(state.filters).filter((k) => isActive(state.filters[k]))
  const hasSearch = state.search.trim() !== ''

  if (!hasSearch && activeKeys.length === 0) return null

  return (
    <div className="flex flex-wrap items-center gap-1.5 rounded border border-dashed bg-muted/20 px-2 py-1.5 text-xs">
      <span className="text-muted-foreground mr-1">Aktywne:</span>
      {hasSearch && (
        <Chip label={`Szukaj: "${state.search}"`} onRemove={onClearSearch} />
      )}
      {activeKeys.map((k) => {
        const col = colByKey.get(k)
        const v = state.filters[k]
        return (
          <Chip
            key={k}
            label={`${col?.header ?? k}: ${formatFilter(col, v)}`}
            onRemove={() => onRemoveFilter(k)}
          />
        )
      })}
      <button
        type="button"
        onClick={onClearAll}
        className="ml-auto text-muted-foreground hover:text-foreground"
      >
        Wyczyść wszystkie
      </button>
    </div>
  )
}

function Chip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <span className="inline-flex items-center gap-1 rounded bg-background border px-2 py-0.5">
      {label}
      <button type="button" onClick={onRemove} className="text-muted-foreground hover:text-foreground">
        <X className="size-3" />
      </button>
    </span>
  )
}

function formatFilter<TRow>(col: ColumnDef<TRow> | undefined, v: FilterValue): string {
  switch (v.kind) {
    case 'text':
      return `"${v.value}"`
    case 'select': {
      const filter = col?.filter
      if (filter?.kind !== 'select') return v.values.join(', ')
      const labels = v.values.map((val) =>
        filter.options.find((o) => o.value === val)?.label ?? val,
      )
      return labels.join(', ')
    }
    case 'dateRange':
      return `${v.from ?? '...'} → ${v.to ?? '...'}`
    case 'bool':
      if (v.value === null) return 'wszystkie'
      if (col?.filter?.kind === 'bool') {
        return v.value ? (col.filter.trueLabel ?? 'tak') : (col.filter.falseLabel ?? 'nie')
      }
      return v.value ? 'tak' : 'nie'
  }
}

function isActive(value: FilterValue): boolean {
  switch (value.kind) {
    case 'text':
      return value.value.trim() !== ''
    case 'select':
      return value.values.length > 0
    case 'dateRange':
      return value.from !== null || value.to !== null
    case 'bool':
      return value.value !== null
  }
}
